import React, { useState, useEffect, useCallback } from 'react';
import { db, firebase_storage } from '../../firebase/firebaseConfig'; // Asumiendo que la config de Firebase está aquí
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, writeBatch, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'; // Funciones de Storage
import { habitaciones as habitacionesData } from '../../data/habitaciones.js'; // Importar datos locales
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './GestionHabitaciones.css'; // Importar el archivo CSS
import FloatingMenu from '../FloatingMenu';
import SelectorImagenesGaleria from './SelectorImagenesGaleria'; // Importar selector de galería
import Modal from './Modal';
// Importar estilos si es necesario, ej: import './GestionHabitaciones.css';

// Componente para un elemento arrastrable (imagen)
const DraggableImageItem = ({ index, url, moveImage, onDelete, disabled, title, type, badge }) => {
  const [{ isDragging }, dragRef] = useDrag({
    type: 'ROOM_IMAGE_ITEM',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disabled,
  });

  const [, dropRef] = useDrop({
    accept: 'ROOM_IMAGE_ITEM',
    hover: (item, monitor) => {
      if (item.index === index) {
        return;
      }
      moveImage(item.index, index);
      item.index = index;
    },
  });

  return (
    <div 
      ref={(node) => dragRef(dropRef(node))} 
      className="image-preview-item"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <img src={url} alt={`Preview ${index + 1}`} />
      <button
        type="button"
        onClick={onDelete}
        className="btn-remove-single-image"
        disabled={disabled}
        title={title}
      >
        &times;
      </button>
      <div className="image-tag">#{index + 1}</div>
      {type === 'new' && <span className="new-image-badge">Nueva</span>}
      {type === 'gallery' && <span className="gallery-image-badge">Galería</span>}
    </div>
  );
};

// Componente del Formulario para múltiples imágenes
function RoomForm({ initialData, onSave, onCancel, isSaving }) {
  const [formData, setFormData] = useState({
    id: '',
    nombre: '',
    capacity: '',
    tipo: '',
    descripcion: '',
    preciosPorOcupacion: {},
    imagenes: [], // Array de URLs existentes
  });
  const [newImageFiles, setNewImageFiles] = useState([]); // Array de Files nuevos
  const [previews, setPreviews] = useState([]); // Array de objetos { type: 'existing'/'new', url: '...', file: File (opcional) }
  const [galeriaSelectedImages, setGaleriaSelectedImages] = useState([]); // Imágenes seleccionadas de galería

  // Actualizar previews cuando cambian los datos iniciales, las imágenes del form,
  // los nuevos archivos o las imágenes de la galería
  useEffect(() => {
    const generatePreviews = () => {
      const existingPreviews = (formData.imagenes || []).map(url => ({ type: 'existing', url }));
      const newPreviews = newImageFiles.map(file => ({ type: 'new', url: URL.createObjectURL(file), file }));
      const galeriaPreviews = galeriaSelectedImages.map(img => ({ type: 'gallery', url: img.url, galleryImage: img }));
      
      // Limpiar Object URLs anteriores para evitar memory leaks
      previews.forEach(p => { if (p.type === 'new') URL.revokeObjectURL(p.url); });
      setPreviews([...existingPreviews, ...newPreviews, ...galeriaPreviews]);
    };
    generatePreviews();

    // Cleanup function para revocar Object URLs al desmontar o antes de regenerar
    return () => {
      previews.forEach(p => { if (p.type === 'new') URL.revokeObjectURL(p.url); });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.imagenes, newImageFiles, galeriaSelectedImages]); // Depender de las imágenes existentes, los nuevos archivos, y galería

  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id || '',
        nombre: initialData.nombre || '',
        capacity: initialData.capacity || '',
        tipo: initialData.tipo || '',
        descripcion: initialData.descripcion || '',
        preciosPorOcupacion: initialData.preciosPorOcupacion || {},
        imagenes: initialData.imagenes || [], // Cargar array existente
      });
      setNewImageFiles([]); // Resetear nuevos archivos al editar
      setGaleriaSelectedImages([]); // Resetear imágenes de galería
    } else {
      setFormData({ id: '', nombre: '', capacity: '', tipo: '', descripcion: '', preciosPorOcupacion: {}, imagenes: [] });
      setNewImageFiles([]);
      setGaleriaSelectedImages([]);
    }
    // No establecer previews aquí, el otro useEffect se encarga
  }, [initialData]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'capacity' || name === 'id') {
        newValue = Number(value) >= 0 ? Number(value) : ''; // Asegurar número no negativo o vacío
    }

    setFormData(prev => {
        const newState = { ...prev, [name]: newValue };

        // Si cambia la capacidad, resetear/ajustar preciosPorOcupacion
        if (name === 'capacity') {
            const newCapacity = newValue || 0;
            const currentPrices = prev.preciosPorOcupacion || {};
            const newPrices = {};
            for (let i = 1; i <= newCapacity; i++) {
                newPrices[i] = currentPrices[i] || ''; // Mantener precio si existía, sino vacío
            }
            newState.preciosPorOcupacion = newPrices;
        }
        return newState;
    });
  };

  const handlePriceChange = (e) => {
      const { name, value } = e.target;
      // El nombre será algo como "price-1", "price-2"
      const occupancy = name.split('-')[1];
      const price = Number(value) >= 0 ? Number(value) : ''; // Asegurar número no negativo o vacío

      setFormData(prev => ({
          ...prev,
          preciosPorOcupacion: {
              ...prev.preciosPorOcupacion,
              [occupancy]: price
          }
      }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      // Añadir nuevos archivos a la lista existente
      setNewImageFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveExistingImage = (urlToRemove) => {
    // Filtrar la URL del array de imágenes existentes en formData
    setFormData(prev => ({ ...prev, imagenes: prev.imagenes.filter(url => url !== urlToRemove) }));
  };

  const handleRemoveNewImage = (fileToRemove) => {
    // Filtrar el archivo de la lista de nuevos archivos
    setNewImageFiles(prev => prev.filter(file => file !== fileToRemove));
  };

  // Handler para imágenes seleccionadas de galería
  const handleGaleriaImagesSelected = (selectedImages) => {
    setGaleriaSelectedImages(selectedImages);
  };

  // Handle para eliminar una imagen de galería seleccionada
  const handleRemoveGalleryImage = (image) => {
    setGaleriaSelectedImages(prev => prev.filter(img => img.url !== image.url));
  };
  
  // Funciones para mover/reordenar imágenes
  const moveExistingImage = (fromIndex, toIndex) => {
    const updatedUrls = [...formData.imagenes];
    const [movedItem] = updatedUrls.splice(fromIndex, 1);
    updatedUrls.splice(toIndex, 0, movedItem);
    setFormData(prev => ({ ...prev, imagenes: updatedUrls }));
  };

  const moveNewImage = (fromIndex, toIndex) => {
    const updatedFiles = [...newImageFiles];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    setNewImageFiles(updatedFiles);
  };

  const moveGalleryImage = (fromIndex, toIndex) => {
    const updatedImages = [...galeriaSelectedImages];
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    setGaleriaSelectedImages(updatedImages);
  };

  const movePreviewImage = (fromIndex, toIndex) => {
    const preview = previews[fromIndex];
    
    if (preview.type === 'existing') {
      moveExistingImage(
        formData.imagenes.findIndex(url => url === preview.url), 
        formData.imagenes.length > toIndex ? toIndex : formData.imagenes.length - 1
      );
    } else if (preview.type === 'new') {
      moveNewImage(
        newImageFiles.findIndex(file => file === preview.file),
        newImageFiles.length > toIndex - formData.imagenes.length ? 
          toIndex - formData.imagenes.length : newImageFiles.length - 1
      );
    } else if (preview.type === 'gallery') {
      moveGalleryImage(
        galeriaSelectedImages.findIndex(img => img.url === preview.galleryImage.url),
        galeriaSelectedImages.length > toIndex - (formData.imagenes.length + newImageFiles.length) ? 
          toIndex - (formData.imagenes.length + newImageFiles.length) : galeriaSelectedImages.length - 1
      );
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validar que todos los precios por ocupación estén definidos?
    const capacityNum = Number(formData.capacity) || 0;
    let pricesValid = true;
    for(let i = 1; i <= capacityNum; i++) {
        if (formData.preciosPorOcupacion[i] === '' || formData.preciosPorOcupacion[i] === undefined || formData.preciosPorOcupacion[i] < 0) {
            pricesValid = false;
            break;
        }
    }
    if (!pricesValid && capacityNum > 0) {
        // Opcional: Mostrar error al usuario
        alert("Por favor, define un precio válido (mayor o igual a 0) para cada nivel de ocupación.");
        return; // Detener guardado
    }

    // Extraer URLs de imágenes de galería
    const galeriaUrls = galeriaSelectedImages.map(img => img.url);
    
    onSave(formData, newImageFiles, galeriaUrls);
  };

  // Helper para generar los inputs de precio
  const renderPriceInputs = () => {
    const capacityNum = Number(formData.capacity) || 0;
    if (capacityNum <= 0) {
      return <p><small>Define una capacidad mayor a 0 para establecer precios.</small></p>;
    }

    const inputs = [];
    for (let i = 1; i <= capacityNum; i++) {
      inputs.push(
        <div key={i} className="form-group form-group-inline">
          <label htmlFor={`price-${i}`}>{`Precio (${i} ${i === 1 ? 'persona' : 'personas'}):`}</label>
          <input
            type="number"
            id={`price-${i}`}
            name={`price-${i}`}
            value={formData.preciosPorOcupacion[i] || ''} // Usar valor del estado o vacío
            onChange={handlePriceChange}
            min="0"
            step="0.01"
            required // Hacerlos requeridos
            disabled={isSaving}
            placeholder="Ej: 40000"
          />
        </div>
      );
    }
    return inputs;
  };

  return (
    <form onSubmit={handleSubmit} className="room-form">
      <div className="form-group">
        <label htmlFor="id">ID Habitación (Numérico):</label>
        <input
          type="number"
          id="id"
          name="id"
          value={formData.id}
          onChange={handleBasicChange}
          required
          min="1"
          disabled={!!initialData || isSaving}
          placeholder={initialData ? "ID no modificable" : "Ej: 1, 2, 101"}
        />
        {!initialData && <small>Este ID debe ser único y no podrá cambiarse.</small>}
      </div>
      <div className="form-group">
        <label htmlFor="nombre">Nombre Habitación:</label>
        <input
          type="text"
          id="nombre"
          name="nombre"
          value={formData.nombre}
          onChange={handleBasicChange}
          required
          disabled={isSaving}
        />
      </div>
      <div className="form-group">
        <label htmlFor="capacity">Capacidad (Máx. personas):</label>
        <input
          type="number"
          id="capacity"
          name="capacity"
          value={formData.capacity}
          onChange={handleBasicChange}
          min="1"
          required
          disabled={isSaving}
        />
      </div>
      <div className="price-section">
          <h4>Precios por Ocupación (€)</h4>
          {renderPriceInputs()}
      </div>
      <div className="form-group">
        <label>Imágenes Actuales y Nuevas: <small>(Arrastra para reordenar)</small></label>
        <DndProvider backend={HTML5Backend}>
          <div className="image-gallery-preview">
            {previews.length === 0 && <p className="no-image">No hay imágenes seleccionadas.</p>}
            {previews.map((preview, index) => (
              <DraggableImageItem
                key={index}
                index={index}
                url={preview.url}
                type={preview.type}
                moveImage={movePreviewImage}
                onDelete={() => {
                  if (preview.type === 'existing') {
                    handleRemoveExistingImage(preview.url);
                  } else if (preview.type === 'new') {
                    handleRemoveNewImage(preview.file);
                  } else if (preview.type === 'gallery') {
                    handleRemoveGalleryImage(preview.galleryImage);
                  }
                }}
                disabled={isSaving}
                title={
                  preview.type === 'existing' 
                    ? 'Eliminar imagen guardada' 
                    : preview.type === 'new'
                      ? 'Descartar nueva imagen'
                      : 'Quitar imagen de galería'
                }
              />
            ))}
          </div>
        </DndProvider>
      </div>

      <div className="upload-methods">
        <div className="upload-method">
          <label htmlFor="imageUpload">Añadir Nuevas Imágenes:</label>
          <input
            type="file"
            id="imageUpload"
            name="imageUpload"
            accept="image/png, image/jpeg, image/webp"
            multiple // Permitir selección múltiple
            onChange={handleFileChange}
            disabled={isSaving}
          />
        </div>
        
        <div className="upload-method">
          <label>Seleccionar de la Galería:</label>
          <SelectorImagenesGaleria 
            onImagesSelected={handleGaleriaImagesSelected}
            initialSelectedImages={[]}
            multiple={true}
            buttonText="Seleccionar de la Galería"
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-cancelar" disabled={isSaving}>
          Cancelar
        </button>
        <button type="submit" className="btn-guardar" disabled={isSaving}>
          {isSaving ? (
            <><span className="spinner"></span> Guardando...</>
          ) : (initialData ? 'Actualizar' : 'Crear')}
        </button>
      </div>
    </form>
  );
}

function GestionHabitaciones() {
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPopulating, setIsPopulating] = useState(false); // Estado para evitar múltiples poblaciones

  const populateFirestore = useCallback(async () => {
      if (isPopulating || habitacionesData.length === 0) return;

      console.log('Intentando poblar Firestore desde habitaciones.js usando IDs originales...');
      setIsPopulating(true);
      setLoading(true);
      setError('Poblando base de datos inicial desde archivo local...');

      try {
          const batch = writeBatch(db);
          let count = 0;

          habitacionesData.forEach(hab => {
              const newRoomRef = doc(db, 'habitaciones', String(hab.id));
              const dataToUpload = {
                  nombre: hab.nombre || 'Sin Nombre',
                  capacity: hab.capacidad || 1,
                  tipo: hab.tipo || '',
                  descripcion: hab.descripcion || '',
                  preciosPorOcupacion: hab.preciosPorOcupacion || {},
                  imagenes: hab.imagenes || [],
              };
              batch.set(newRoomRef, dataToUpload);
              count++;
          });

          await batch.commit();
          console.log(`${count} habitaciones añadidas/actualizadas en Firestore usando IDs originales.`);
          setSuccess('Base de datos inicial poblada/verificada correctamente desde archivo local.');
          setError('');
      } catch (populateError) {
          console.error("Error poblando Firestore desde habitaciones.js: ", populateError);
          setError('Error al poblar la base de datos inicial.');
          setSuccess('');
      } finally {
          setIsPopulating(false);
      }
  }, [isPopulating]);

  const fetchHabitaciones = useCallback(async (checkAndPopulate = false) => {
    if (isPopulating) return; // No hacer fetch si se está poblando

    console.log(`Fetching habitaciones... checkAndPopulate: ${checkAndPopulate}`);
    setLoading(true);
    let shouldPopulate = false;

    try {
      const habitacionesCollection = collection(db, 'habitaciones');
      const habitacionesSnapshot = await getDocs(habitacionesCollection);
      const habitacionesList = habitacionesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setHabitaciones(habitacionesList);
      setError(''); // Limpiar error si el fetch es exitoso

      if (checkAndPopulate && habitacionesList.length === 0) {
        shouldPopulate = true;
      }

    } catch (err) {
      console.error("Error fetching rooms: ", err);
      setError('Error al cargar las habitaciones.');
      setHabitaciones([]); // Limpiar habitaciones si hay error
    } finally {
        // Importante: poner setLoading(false) solo si NO vamos a poblar
        if (!shouldPopulate) {
            setLoading(false);
        }
    }

    // Si determinamos que hay que poblar, llamar a la función separada
    if (shouldPopulate) {
        await populateFirestore();
        // Después de poblar (o intentar), volver a cargar definitivamente
        await fetchHabitaciones(false); // Llamar recursivamente SIN checkAndPopulate
    }

  }, [isPopulating, populateFirestore]); // Añadir dependencias

  // Cargar inicial y posible población
  useEffect(() => {
    fetchHabitaciones(true);
  }, [fetchHabitaciones]); // fetchHabitaciones ahora es la dependencia correcta

  const handleEditClick = (habitacion) => {
    setCurrentRoom(habitacion);
    setError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const handleAddNewClick = () => {
    setCurrentRoom(null);
    setError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const handleSave = async (formData, newImageFiles, galeriaUrls = []) => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    const formId = formData.id;
    const dataToSave = { ...formData };
    delete dataToSave.id; // Quitar id del objeto a guardar

    // URLs de imágenes existentes que se CONSERVARÁN (las que quedan en formData.imagenes)
    const keptExistingImageUrls = formData.imagenes || [];
    // URLs de imágenes que estaban al PRINCIPIO de la edición (para saber cuáles borrar)
    const originalImageUrls = currentRoom?.imagenes || [];
    // URLs a borrar de Storage: las originales que NO están en las conservadas
    const urlsToDeleteFromStorage = originalImageUrls.filter(url => !keptExistingImageUrls.includes(url));

    try {
      // --- Validación ID (solo crear) ---
      if (!currentRoom) {
        if (!formId || formId <= 0) {
          setError('Debes proporcionar un ID numérico positivo único para la nueva habitación.');
          setIsSaving(false);
          return;
        }
        const potentialDocRef = doc(db, 'habitaciones', String(formId));
        const docSnap = await getDoc(potentialDocRef);
        if (docSnap.exists()) {
          setError(`El ID de habitación '${formId}' ya existe. Por favor, elige otro.`);
          setIsSaving(false);
          return;
        }
      }
      // --- Fin Validación ---

      // --- Subida de Nuevas Imágenes ---
      const uploadedImageUrls = [];
      if (newImageFiles && newImageFiles.length > 0) {
        console.log(`Subiendo ${newImageFiles.length} nuevas imágenes...`);
        
        try {
          // Importar dinámicamente la función subirImagenGaleria para la galería
          const { subirImagenGaleria } = await import('../../data/galeria');
          
          const uploadPromises = newImageFiles.map(async (file) => {
            const fileName = `habitaciones/${formId || currentRoom.id}_${Date.now()}_${file.name}`;
            const storageRef = ref(firebase_storage, fileName);
            
            // Subir a Storage para las habitaciones
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);
            
            // También agregar a la galería compartida
            try {
              // Crear metadatos para la galería
              const metadata = {
                etiquetas: ['habitacion', `habitacion-${formId || currentRoom?.id || 'nueva'}`],
                descripcion: `Imagen de habitación ${formData.nombre || ''} (ID: ${formId || currentRoom?.id || 'nueva'})`
              };
              
              // Agregar a la galería sin carpeta específica (root)
              await subirImagenGaleria(
                file,      // El archivo original
                metadata,  // Metadatos para galería
                null       // Sin carpeta específica (root)
              );
              console.log(`Imagen ${file.name} agregada a la galería principal`);
            } catch (galeriaError) {
              // Si falla al agregar a la galería, solo registramos el error pero no abortamos
              console.error("Error al agregar imagen a la galería:", galeriaError);
            }
            
            console.log('Subida completa:', downloadUrl);
            return downloadUrl;
          });
          
          // Esperar a que todas las subidas terminen
          uploadedImageUrls.push(...await Promise.all(uploadPromises));
          console.log('URLs subidas:', uploadedImageUrls);
        } catch (uploadError) {
          console.error('Error al subir imágenes:', uploadError);
          throw uploadError;
        }
      }
      // --- Fin Subida ---

      // Construir el array final de imágenes para Firestore, manteniendo el orden exacto
      // keptExistingImageUrls ya está en el orden correcto después del drag & drop
      dataToSave.imagenes = [...keptExistingImageUrls, ...uploadedImageUrls, ...galeriaUrls];

       // --- Borrado de Imágenes Antiguas de Storage ---
       if (urlsToDeleteFromStorage.length > 0) {
        console.log('Eliminando imágenes de Storage:', urlsToDeleteFromStorage);
        const deletePromises = urlsToDeleteFromStorage.map(async (url) => {
          try {
            const imageRef = ref(firebase_storage, url);
            await deleteObject(imageRef);
            console.log('Imagen eliminada:', url);
          } catch (deleteError) {
            // No detener todo si falla un borrado, solo advertir
            console.warn(`Error eliminando imagen ${url} de Storage:`, deleteError);
          }
        });
        await Promise.all(deletePromises); // Esperar a que terminen los intentos de borrado
      }
      // --- Fin Borrado Storage ---

      // --- Guardado en Firestore --- 
      if (currentRoom && currentRoom.id) {
        // ACTUALIZAR
        const roomRef = doc(db, 'habitaciones', currentRoom.id);
        await updateDoc(roomRef, dataToSave);
        setSuccess('Habitación actualizada correctamente.');
      } else {
        // CREAR
        const newRoomRef = doc(db, 'habitaciones', String(formId));
        await setDoc(newRoomRef, dataToSave);
        setSuccess('Habitación creada correctamente.');
      }
      // --- Fin Guardado Firestore ---

      setIsModalOpen(false);
      await fetchHabitaciones();
    } catch (err) {
      console.error("Error saving room: ", err);
      setError('Error al guardar la habitación. Inténtalo de nuevo.');
      // Aquí sería ideal intentar borrar las imágenes recién subidas si falla Firestore...
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (roomId) => {
    const roomToDelete = habitaciones.find(h => h.id === roomId);
    const imagesToDelete = roomToDelete?.imagenes || []; // Array de URLs a borrar

    if (window.confirm('¿Estás seguro de que quieres eliminar esta habitación? Se eliminarán también TODAS sus imágenes asociadas.')) {
      setIsSaving(true);
      setError('');
      setSuccess('');
      try {
        // 1. Eliminar documento de Firestore
        const roomRef = doc(db, 'habitaciones', roomId);
        await deleteDoc(roomRef);
        console.log('Habitación eliminada de Firestore:', roomId);

        // 2. Eliminar TODAS las imágenes de Storage asociadas
        if (imagesToDelete.length > 0) {
           console.log('Eliminando imágenes asociadas de Storage:', imagesToDelete);
           const deletePromises = imagesToDelete.map(async (url) => {
              try {
                 const imageRef = ref(firebase_storage, url);
                 await deleteObject(imageRef);
              } catch (storageError) {
                 console.warn(`Error eliminando imagen ${url} de Storage:`, storageError);
              }
           });
           await Promise.all(deletePromises);
        }

        setSuccess('Habitación eliminada correctamente.');
        await fetchHabitaciones();
      } catch (err) {
        console.error("Error deleting room: ", err);
        setError('Error al eliminar la habitación.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  if (loading) {
    return <div className="loading-container">Cargando gestión de habitaciones...</div>;
  }

  return (
    <div className="gestion-habitaciones-container">
      <h2>Gestión de Habitaciones</h2>

      {error && <div className="mensaje mensaje-error">{error}</div>}
      {success && <div className="mensaje mensaje-exito">{success}</div>}

      <button onClick={handleAddNewClick} className="btn-add-new" disabled={isSaving}>
        + Añadir Nueva Habitación
      </button>

      <div className="habitaciones-list">
        <h3>Listado de Habitaciones</h3>
        {habitaciones.length === 0 ? (
          <p>No hay habitaciones para mostrar.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Imagen</th>
                <th>ID</th>
                <th>Nombre</th>
                <th>Capacidad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {habitaciones.map((hab) => (
                <tr key={hab.id}>
                  <td>
                    {hab.imagenes && hab.imagenes.length > 0 ? (
                      <img src={hab.imagenes[0]} alt={hab.nombre || 'Habitación'} className="room-thumbnail" />
                    ) : (
                      <span className="no-image">Sin imagen</span>
                    )}
                  </td>
                  <td>{hab.id}</td>
                  <td>{hab.nombre || 'N/A'}</td>
                  <td>{hab.capacity || 'N/A'}</td>
                  <td>
                    <button onClick={() => handleEditClick(hab)} className="btn-edit" disabled={isSaving}>
                      Editar
                    </button>
                    <button onClick={() => handleDelete(hab.id)} className="btn-delete" disabled={isSaving}>
                       Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentRoom ? 'Editar Habitación' : 'Añadir Nueva Habitación'}
        closeOnOutsideClick={false}
      >
        <RoomForm
          initialData={currentRoom}
          onSave={handleSave}
          onCancel={() => setIsModalOpen(false)}
          isSaving={isSaving}
        />
      </Modal>

      {/* Componente de menú flotante */}
      <FloatingMenu />
    </div>
  );
}

export default GestionHabitaciones; 